"use client"

import { useState, useEffect } from "react"
import "./OrdersPage.css"
import { getDatabase, ref, get } from "firebase/database"
import { getAuth } from "firebase/auth"
import { Link } from "react-router-dom"

export default function OrdersPage() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedOrder, setExpandedOrder] = useState(null)

  // Get current user and database
  const auth = getAuth()
  const database = getDatabase()

  useEffect(() => {
    const fetchOrders = async () => {
      if (!auth.currentUser) {
        setLoading(false)
        setError("Please log in to view your orders")
        return
      }

      try {
        // Get user's orders from Realtime Database
        const userOrdersRef = ref(database, `users/${auth.currentUser.uid}/orders`)
        const userOrdersSnapshot = await get(userOrdersRef)

        if (userOrdersSnapshot.exists()) {
          const orderIds = Object.keys(userOrdersSnapshot.val())

          const orderPromises = orderIds.map(async (orderId) => {
            const orderRef = ref(database, `orders/${orderId}`)
            const orderSnapshot = await get(orderRef)

            if (orderSnapshot.exists()) {
              const orderData = orderSnapshot.val()
              return {
                id: orderId,
                ...orderData,
                createdAt: new Date(orderData.createdAt),
              }
            }
            return null
          })

          const fetchedOrders = (await Promise.all(orderPromises))
            .filter((order) => order !== null)
            .sort((a, b) => b.createdAt - a.createdAt)

          setOrders(fetchedOrders)
        } else {
          setOrders([])
        }
      } catch (error) {
        console.error("Error fetching orders:", error)
        setError("Failed to load orders. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [auth.currentUser, database])

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusClass = (status) => {
    switch (status) {
      case "processing":
        return "status-processing"
      case "confirmed":
        return "status-confirmed"
      case "preparing":
        return "status-preparing"
      case "ready":
        return "status-ready"
      case "completed":
        return "status-completed"
      default:
        return "status-processing"
    }
  }

  const toggleOrderDetails = (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null)
    } else {
      setExpandedOrder(orderId)
    }
  }

  if (loading) {
    return (
      <div className="orders-loading">
        <div className="spinner"></div>
        <p>Loading your orders...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="orders-error">
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/login">
          <button className="orders-button">Log In</button>
        </Link>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="orders-empty">
        <h2>No Orders Found</h2>
        <p>You haven't placed any orders yet.</p>
        <Link to="/catalog">
          <button className="orders-button">Start Shopping</button>
        </Link>
      </div>
    )
  }

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h1>My Orders</h1>
        <p>View and manage your order history</p>
      </div>

      <div className="orders-list">
        {orders.map((order) => (
          <div key={order.id} className="order-card">
            <div className="order-card-header" onClick={() => toggleOrderDetails(order.id)}>
              <div className="order-card-header-left">
                <h3>Order #{order.orderNumber}</h3>
                <p className="order-date">
                  {formatDate(order.createdAt)} at {formatTime(order.createdAt)}
                </p>
              </div>
              <div className="order-card-header-right">
                <span className={`order-status ${getStatusClass(order.status)}`}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
                <span className="order-total">₹{order.totals.total.toFixed(2)}</span>
                <button className="order-toggle-btn">
                  {expandedOrder === order.id ? "Hide Details" : "Show Details"}
                </button>
              </div>
            </div>

            {expandedOrder === order.id && (
              <div className="order-details">
                <div className="order-details-grid">
                  <div className="order-address">
                    <h4>Shipping Address</h4>
                    <p>{order.address.street}</p>
                    <p>
                      {order.address.city}, {order.address.state} {order.address.zipCode}
                    </p>
                    <p>Phone: {order.address.phone}</p>
                  </div>

                  <div className="order-summary">
                    <h4>Order Summary</h4>
                    <div className="summary-row">
                      <span>Subtotal:</span>
                      <span>₹{order.totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Tax:</span>
                      <span>₹{order.totals.tax.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Shipping:</span>
                      <span>₹{order.totals.shipping.toFixed(2)}</span>
                    </div>
                    <div className="summary-row total">
                      <span>Total:</span>
                      <span>₹{order.totals.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="order-items">
                  <h4>Items</h4>
                  <div className="items-grid">
                    {order.items.map((item) => (
                      <div key={item.id} className="order-item">
                        <div className="item-image">
                          <img src={item.image || "/placeholder.svg?height=80&width=80"} alt={item.name} />
                        </div>
                        <div className="item-details">
                          <h5>{item.name}</h5>
                          <p className="item-price">
                            ₹{item.price.toFixed(2)} × {item.quantity}
                          </p>
                          <p className="item-total">₹{(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="order-actions">
                  <Link to={`/tracking?order=${order.orderNumber}`}>
                    <button className="track-order-btn">Track Order</button>
                  </Link>
                  <button className="reorder-btn">Reorder</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

